import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useMutation } from "react-query";
import { AiOutlineDisconnect } from "react-icons/ai";
import { FiMoreVertical } from "react-icons/fi";
import { reportBrokenLink } from "../../../services/readingOrdersApi";
import { trackEvent } from "../../../shared/analytics";

const dialogProps = {
  bg: "#171C2D",
  borderWidth: "1px",
  borderColor: "#30384E",
  borderRadius: "14px",
  mx: "4",
  maxW: "420px",
};

// Mirrors the mobile ComicDetailNavbarMenu + ComicNavMenu pair: a kebab button
// in the header that opens the comic options sheet, and the broken link
// confirmation it leads to.
const ComicOptionsMenu = ({ comic }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmingReport, setConfirmingReport] = useState(false);
  const toast = useToast({ duration: 5000, isClosable: true });
  const comicTitle = comic?.title || "This comic";

  const reportMutation = useMutation((comicId) => reportBrokenLink(comicId), {
    onSuccess: () => {
      toast({
        title: "Report sent",
        description: "Thank you! The Redwing team will look into it.",
        status: "success",
      });
      closeMenu();
    },
    onError: () => {
      toast({
        title: "Could not send report",
        description: "Please try again.",
        status: "error",
      });
    },
  });

  function closeMenu() {
    if (reportMutation.isLoading) {
      return;
    }
    setConfirmingReport(false);
    onClose();
  }

  const closeConfirmation = () => {
    if (!reportMutation.isLoading) {
      setConfirmingReport(false);
    }
  };

  const onConfirmReport = () => {
    trackEvent("comic_details_nav_broken_link_reported");
    reportMutation.mutate(comic?.id);
  };

  return (
    <>
      <IconButton
        aria-label="Comic options"
        icon={<FiMoreVertical size="25px" />}
        variant="ghost"
        color="#FFFFFF"
        borderRadius="full"
        _hover={{ bg: "rgba(255, 255, 255, 0.14)" }}
        onClick={onOpen}
      />

      <Modal isOpen={isOpen} onClose={closeMenu} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.75)" />
        {confirmingReport ? (
          <ModalContent {...dialogProps}>
            <ModalBody p="18px">
              <Center
                w="48px"
                h="48px"
                mb="14px"
                borderRadius="full"
                bg="rgba(255, 170, 0, 0.12)"
              >
                <AiOutlineDisconnect color="#FFAA00" size="26px" />
              </Center>
              <Text
                color="#F7F9FC"
                fontSize="20px"
                lineHeight="25px"
                fontWeight="600"
              >
                Report broken link?
              </Text>
              <Text mt="6px" color="#A8B2C8" fontSize="13px" lineHeight="19px">
                Let us know if the reading link for this comic no longer works.
              </Text>
              <HStack mt="22px" spacing="10px">
                <Button
                  flex="1"
                  variant="outline"
                  colorScheme="gray"
                  isDisabled={reportMutation.isLoading}
                  onClick={closeConfirmation}
                >
                  Cancel
                </Button>
                <Button
                  flex="1"
                  bg="#FFAA00"
                  color="#101426"
                  _hover={{ bg: "#E69900" }}
                  isDisabled={reportMutation.isLoading}
                  onClick={onConfirmReport}
                >
                  {reportMutation.isLoading ? "Sending…" : "Send report"}
                </Button>
              </HStack>
            </ModalBody>
          </ModalContent>
        ) : (
          <ModalContent {...dialogProps}>
            <ModalBody p="18px">
              <Flex alignItems="flex-start" mb="16px">
                <Box flex="1" minW={0} pr="12px">
                  <Text
                    color="#F7F9FC"
                    fontSize="20px"
                    lineHeight="25px"
                    fontWeight="600"
                  >
                    Comic options
                  </Text>
                  <Text
                    mt="3px"
                    color="#A8B2C8"
                    fontSize="13px"
                    lineHeight="18px"
                    noOfLines={2}
                  >
                    {comicTitle}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Close comic options"
                  icon={<CloseIcon boxSize="12px" />}
                  w="36px"
                  h="36px"
                  minW="36px"
                  borderRadius="full"
                  bg="#222B45"
                  color="#A8B2C8"
                  _hover={{ opacity: 0.7 }}
                  onClick={closeMenu}
                />
              </Flex>

              <Box
                borderWidth="1px"
                borderColor="#30384E"
                borderRadius="10px"
                bg="#1A2138"
                overflow="hidden"
              >
                <Flex
                  as="button"
                  type="button"
                  w="100%"
                  minH="58px"
                  px="14px"
                  py="12px"
                  alignItems="center"
                  textAlign="left"
                  _hover={{ bg: "#222B45" }}
                  onClick={() => {
                    trackEvent("comic_details_nav_broken_link_click");
                    setConfirmingReport(true);
                  }}
                >
                  <Box flexShrink={0} mr="12px">
                    <AiOutlineDisconnect color="#FFAA00" size="20px" />
                  </Box>
                  <Box flex="1" minW={0}>
                    <Text color="#F7F9FC" fontSize="15px" lineHeight="20px">
                      Report broken link
                    </Text>
                    <Text color="#8F9BB3" fontSize="12px" lineHeight="16px">
                      Tell us if the reading link no longer works
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </ModalBody>
          </ModalContent>
        )}
      </Modal>
    </>
  );
};

export default ComicOptionsMenu;
